import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  type Auth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  type User,
  sendPasswordResetEmail,
} from "firebase/auth";

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let initPromise: Promise<{ app: FirebaseApp; auth: Auth }> | null = null;

export async function initFirebase(): Promise<{ app: FirebaseApp; auth: Auth }> {
  if (app && auth) return { app, auth };
  if (initPromise) return initPromise;

  initPromise = (async () => {
    const base = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";
    const res = await fetch(`${base}/api/config`);
    if (!res.ok) throw new Error("Failed to load Firebase config");
    const { firebase: config } = (await res.json()) as {
      firebase: {
        apiKey: string;
        authDomain: string;
        projectId: string;
        appId: string;
      };
    };
    app = initializeApp(config);
    auth = getAuth(app);
    return { app, auth };
  })();

  return initPromise;
}

export function getFirebaseAuth(): Auth {
  if (!auth) throw new Error("Firebase not initialized — call initFirebase() first");
  return auth;
}

export async function signInWithGoogle(): Promise<User> {
  const { auth } = await initFirebase();
  const provider = new GoogleAuthProvider();
  const result = await signInWithPopup(auth, provider);
  return result.user;
}

export async function signInWithEmail(
  email: string,
  password: string,
): Promise<User> {
  const { auth } = await initFirebase();
  const result = await signInWithEmailAndPassword(auth, email, password);
  return result.user;
}

export async function registerWithEmail(
  email: string,
  password: string,
): Promise<User> {
  const { auth } = await initFirebase();
  const result = await createUserWithEmailAndPassword(auth, email, password);
  return result.user;
}

export async function signOut(): Promise<void> {
  const { auth } = await initFirebase();
  await firebaseSignOut(auth);
}

export async function resetPassword(email: string): Promise<void> {
  const { auth } = await initFirebase();
  await sendPasswordResetEmail(auth, email);
}

export async function getIdToken(): Promise<string | null> {
  const { auth } = await initFirebase();
  return auth.currentUser?.getIdToken() ?? null;
}

export { onAuthStateChanged, type User };
